import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { Box, CssBaseline, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useI18n } from '../../../application/language/useI18n';
import { inject } from '../../../common/container/inject';
import AccessDeniedPage from '../../../common/errorPage/Page403/AccessDenied';
import { ICommonProperties } from '../../../common/properties/ICommonProperties';
import { UserSystemRoleModelPermissionMap } from '../../../service/systemRole/entity/actions/UserSystemRoleModelPermissionMap';
import { LayoutDrawer } from '../common/drawer/LayoutDrawer';
import { LayoutModalWindow } from '../common/modalWindow/LayoutModalWindows';
import { NotificationHandler, NotificationWrapper } from '../common/notification/Notification';
import { LayoutLoading } from './parts/LayoutLoading';
import { LeftNavigation } from './parts/LeftNavigation/LeftNavigation';
import { Scroller } from './parts/Scroller/Scroller';
import { IMainLayoutDomainStore, MainLayoutDomainStoreToken } from './store/domain/IMainLayoutDomainStore';
import { LayoutDomainContext } from './store/domain/LayoutDomainContext';

export interface IMainLayoutProperties extends ICommonProperties {
  children?: any;
  pageTitle?: string;
  allowedPermissions?: UserSystemRoleModelPermissionMap[];
  showNavBar?: boolean;
  showPaddings?: boolean;
  overflowY?: boolean;
}

export const MainLayout = observer(
  ({
    children,
    pageTitle = '',
    allowedPermissions = [],
    showNavBar = true,
    showPaddings = true,
    overflowY = false,
  }: IMainLayoutProperties) => {
    const { translate } = useI18n();
    const location = useLocation();
    const [layoutDomainStore] = useState<IMainLayoutDomainStore>(inject(MainLayoutDomainStoreToken));
    const uiStore = layoutDomainStore.ui;
    const classes = useStyles();
    const isLoading = layoutDomainStore.ui.isBootLoading.value;
    const isRenderContent = !isLoading;
    const isSettingsChildPage = location.pathname.startsWith('/settings/') && location.pathname !== '/settings';
    useEffect(() => {
      layoutDomainStore.loadLayoutData();
    }, [layoutDomainStore]);

    useEffect(() => {
      layoutDomainStore.checkPermissions(allowedPermissions);
    }, [layoutDomainStore, allowedPermissions, layoutDomainStore.ui.activeUser.entity]);
    useEffect(() => {
      layoutDomainStore.setPageTitle(pageTitle);
    }, [layoutDomainStore, pageTitle]);

    return (
      <Scroller
        detections={layoutDomainStore.ui.scrollDetection.value}
        removeElement={(element) => {
          layoutDomainStore.ui.removeDetections([element]);
        }}
      >
        <NotificationWrapper>
          {isRenderContent && (
            <div className={classes.root}>
              <CssBaseline />
              {showNavBar && uiStore.isAllowedUser.value && (
                <LeftNavigation layoutUser={uiStore.activeUser.entity} layoutDomain={layoutDomainStore} />
              )}
              <main
                style={{
                  padding: showPaddings ? layoutDomainStore.ui.pagePadding.value : 0,
                  height: '100%',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
                className={classes.content}
              >
                <LayoutDomainContext.Provider value={layoutDomainStore}>
                  {isSettingsChildPage && layoutDomainStore.ui.isAllowedUser.value && (
                    <Box
                      className={classes.backToSettingsWrap}
                      sx={{
                        flexShrink: 0,
                        paddingLeft: layoutDomainStore.ui.pagePadding.value === 0 ? '24px' : 0,
                        paddingTop: layoutDomainStore.ui.pagePadding.value === 0 ? '24px' : 0,
                      }}
                    >
                      <Link className={classes.backToSettingsLink} to="/settings">
                        <ArrowBackRoundedIcon fontSize="small" />
                        <span>{translate('phrases.backToSettings')}</span>
                      </Link>
                    </Box>
                  )}
                  <Box sx={{ flex: 1, minHeight: 0, overflow: overflowY ? 'auto' : 'hidden' }}>
                    {layoutDomainStore.ui.isAllowedUser.value && children}
                    {!layoutDomainStore.ui.isAllowedUser.value && <AccessDeniedPage />}
                  </Box>
                </LayoutDomainContext.Provider>
              </main>
            </div>
          )}
          {isLoading && <LayoutLoading />}
          <LayoutModalWindow layoutDomain={layoutDomainStore} />
          <LayoutDrawer layoutDomain={layoutDomainStore} />
          <NotificationHandler layoutDomain={layoutDomainStore} />
        </NotificationWrapper>
      </Scroller>
    );
  },
);

export const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
    },
    content: {
      flexGrow: 1,
      backgroundColor: theme.palette.background.default,
      paddingTop: theme.spacing(2),
      overflowX: 'hidden',
      width: '100%',
    },
    backToSettingsWrap: {
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(1),
    },
    backToSettingsLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      color: theme.palette.primary.main,
      textDecoration: 'none',
      fontWeight: 500,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    toolbar: theme.mixins.toolbar,
  };
});
